import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Wallet, Users, ArrowUpRight, ArrowDownLeft, Sparkles, Calendar, BarChart3, MessageCircle } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, CartesianGrid } from "recharts";
import api, { fmtNaira, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ScoreCard from "@/components/ScoreCard";
import DailyDiary from "@/components/DailyDiary";
import ReportPreview from "@/components/ReportPreview";
import BusinessInsights from "@/components/BusinessInsights";
import WalletSummary from "@/components/WalletSummary";
import MorningBrief from "@/components/MorningBrief";
import BusinessCoach from "@/components/BusinessCoach";
import NotificationBell from "@/components/NotificationBell";
import { useNavigate } from "react-router-dom";
import { useT } from "@/lib/i18n";

export default function Dashboard() {
  const { user } = useAuth();
  const t = useT(user?.preferred_language);
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [period, setPeriod] = useState("today");
  const [loading, setLoading] = useState(true);
  const [showDiary, setShowDiary] = useState(false);
  const [reportKind, setReportKind] = useState(null); // 'weekly' | 'monthly' | null

  useEffect(() => {
    const load = () =>
      Promise.all([api.get("/cashflow/summary"), api.get("/transactions?limit=10")])
        .then(([s, t]) => { setSummary(s.data); setTxs(t.data.transactions || []); })
        .finally(() => setLoading(false));
    load();
    // Iter33 live sync — a WhatsApp write should reflect on the dashboard instantly.
    const onLive = (e) => {
      const kind = e?.detail?.kind;
      if (kind === "transaction" || kind === "brain_sync") load();
    };
    window.addEventListener("kobolog:live", onLive);
    return () => window.removeEventListener("kobolog:live", onLive);
  }, []);

  if (loading) return <div className="p-6 text-center text-kudi-ink/50">{t("loadingNumbers")}</div>;
  if (!summary) return <div className="p-6">No data yet.</div>;

  const periodData = summary[period];

  return (
    <div className="px-4 py-5 space-y-5 pb-6">
      {/* Greeting */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-kudi-ink/55 uppercase tracking-wider font-semibold">{new Date().toLocaleDateString("en-NG", { weekday: "long", day: "numeric", month: "long" })}</div>
          <h1 className="font-heading text-2xl font-bold mt-1">{t("welcome")}, {user?.owner_name?.split(" ")[0] || "boss"} 👋</h1>
          <p className="text-sm text-kudi-ink/60 mt-0.5">{t("moneyPulse")}</p>
        </div>
        <NotificationBell />
      </div>

      {/* Morning brief (period-aware) — matches mobile app card */}
      <MorningBrief onAskAi={() => nav("/app/chat")} />

      {/* Business coach — matches mobile app card */}
      <BusinessCoach />

      {/* Cash on hand hero card */}
      <div className="rounded-2xl bg-gradient-to-br from-kudi-green to-kudi-green-dark text-white p-5 relative overflow-hidden" data-testid="cash-on-hand-card">
        <div className="absolute inset-0 ankara-watermark opacity-10" aria-hidden />
        <div className="relative">
          <div className="text-xs text-white/75 uppercase tracking-wider">{t("cashPosition")}</div>
          <div className="font-heading text-4xl font-bold mt-1 tabular-nums">{fmtNaira(summary.cash_on_hand)}</div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="bg-white/10 backdrop-blur rounded-xl p-3 border border-white/15">
              <div className="text-white/70 text-xs">{t("owedToYou")}</div>
              <div className="font-heading text-lg font-semibold mt-0.5 tabular-nums">{fmtNaira(summary.total_owed_to_me)}</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-3 border border-white/15">
              <div className="text-white/70 text-xs">{t("activeDebtors")}</div>
              <div className="font-heading text-lg font-semibold mt-0.5">{summary.active_debtors || 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Period switcher */}
      <div className="flex gap-2" data-testid="period-switcher">
        {["today", "week", "month"].map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`flex-1 py-2 rounded-full text-sm font-medium capitalize transition ${period === p ? "bg-kudi-green text-white" : "bg-white border border-border text-kudi-ink/70"}`}
            data-testid={`period-${p}`}
          >
            {t(p)}
          </button>
        ))}
      </div>

      <ScoreCard />

      <WalletSummary />

      <BusinessInsights />

      <button
        onClick={() => setShowDiary(true)}
        className="w-full bg-gradient-to-r from-kudi-terracotta to-kudi-green text-white rounded-2xl p-4 flex items-center gap-3 hover:shadow-lg transition active:scale-[0.99] relative overflow-hidden"
        data-testid="daily-diary-btn"
      >
        <div className="absolute inset-0 noise-overlay opacity-40" aria-hidden />
        <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center relative">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 text-left relative">
          <div className="font-heading font-semibold">Today's Money Diary 📱</div>
          <div className="text-xs text-white/80 mt-0.5">Share your daily wins on WhatsApp Status</div>
        </div>
        <div className="text-xs font-semibold bg-white/20 backdrop-blur rounded-full px-2 py-1 relative">Open</div>
      </button>

      {showDiary && <DailyDiary onClose={() => setShowDiary(false)} />}

      {/* Reports + WhatsApp Preview row */}
      <div className="grid grid-cols-3 gap-2" data-testid="reports-row">
        <button onClick={() => setReportKind("weekly")} className="bg-white border border-border rounded-2xl p-3 flex flex-col items-center gap-1.5 hover:bg-accent/40 transition active:scale-[0.98]" data-testid="weekly-report-btn">
          <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <BarChart3 className="w-4 h-4" />
          </div>
          <div className="text-[11px] font-semibold text-kudi-ink">{t("weeklyReport")}</div>
        </button>
        <button onClick={() => setReportKind("monthly")} className="bg-white border border-border rounded-2xl p-3 flex flex-col items-center gap-1.5 hover:bg-accent/40 transition active:scale-[0.98]" data-testid="monthly-report-btn">
          <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
            <Calendar className="w-4 h-4" />
          </div>
          <div className="text-[11px] font-semibold text-kudi-ink">{t("monthlyReport")}</div>
        </button>
        <button onClick={() => nav("/app/whatsapp")} className="bg-emerald-600 text-white rounded-2xl p-3 flex flex-col items-center gap-1.5 hover:bg-emerald-700 transition active:scale-[0.98]" data-testid="whatsapp-preview-btn">
          <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
            <MessageCircle className="w-4 h-4" />
          </div>
          <div className="text-[11px] font-semibold">WhatsApp</div>
        </button>
      </div>

      {reportKind && <ReportPreview kind={reportKind} onClose={() => setReportKind(null)} />}

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard label={t("sales")} value={fmtNaira(periodData.sales)} icon={TrendingUp} color="text-emerald-700" bg="bg-emerald-50" testid="stat-sales" />
        <StatCard label={t("expenses")} value={fmtNaira(periodData.expenses)} icon={TrendingDown} color="text-rose-700" bg="bg-rose-50" testid="stat-expenses" />
        <StatCard label={t("net")} value={fmtNaira(periodData.net)} icon={Wallet} color="text-kudi-green" bg="bg-accent" testid="stat-net" />
        <StatCard label={t("paymentsIn")} value={fmtNaira(periodData.payments_received)} icon={ArrowDownLeft} color="text-amber-700" bg="bg-amber-50" testid="stat-payments" />
      </div>

      {/* Chart */}
      <div className="bg-white border border-border rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-heading font-semibold">{t("last7Days")}</div>
            <div className="text-xs text-kudi-ink/55">{t("salesVsExpenses")}</div>
          </div>
        </div>
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={summary.daily_trend} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" vertical={false} />
              <XAxis dataKey="date" axisLine={false} tickLine={false} fontSize={11} stroke="#5C5C5C" />
              <Tooltip
                cursor={{ fill: "rgba(17,94,62,0.05)" }}
                contentStyle={{ borderRadius: 12, border: "1px solid #E5E5E0", fontSize: 12 }}
                formatter={(v) => fmtNaira(v)}
              />
              <Bar dataKey="sales" fill="#115E3E" radius={[6, 6, 0, 0]} />
              <Bar dataKey="expenses" fill="#D96C4A" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent activity */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="font-heading font-semibold">{t("recentActivity")}</div>
          <div className="text-xs text-kudi-ink/55">{txs.length} {t("entries")}</div>
        </div>
        <div className="space-y-2" data-testid="recent-transactions">
          {txs.length === 0 ? (
            <div className="bg-white border border-border rounded-2xl p-6 text-center text-sm text-kudi-ink/50">
              {t("noTxYet")}
            </div>
          ) : txs.map((t) => <TxRow key={t.id} t={t} />)}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, bg, testid }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-4" data-testid={testid}>
      <div className={`w-8 h-8 rounded-lg ${bg} ${color} flex items-center justify-center mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-xs text-kudi-ink/55">{label}</div>
      <div className="font-heading text-lg font-bold mt-0.5 tabular-nums truncate">{value}</div>
    </div>
  );
}

function TxRow({ t }) {
  const config = {
    sale: { icon: ArrowUpRight, color: "text-emerald-700", bg: "bg-emerald-50", sign: "+" },
    expense: { icon: ArrowDownLeft, color: "text-rose-700", bg: "bg-rose-50", sign: "−" },
    debt: { icon: Users, color: "text-amber-700", bg: "bg-amber-50", sign: "" },
    payment_received: { icon: ArrowDownLeft, color: "text-kudi-green", bg: "bg-accent", sign: "+" },
    stock_added: { icon: ArrowUpRight, color: "text-blue-700", bg: "bg-blue-50", sign: "" },
  }[t.type] || { icon: Wallet, color: "text-kudi-ink", bg: "bg-muted", sign: "" };

  return (
    <div className="bg-white border border-border rounded-2xl p-3 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl ${config.bg} ${config.color} flex items-center justify-center shrink-0`}>
        <config.icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{t.description || t.type.replace("_", " ")}</div>
        <div className="text-xs text-kudi-ink/50 mt-0.5">
          {t.customer_name && <span>{t.customer_name} · </span>}
          {fmtDate(t.created_at)}
          {t.type === "debt" && !t.is_paid && <span className="ml-1 text-amber-700">· unpaid</span>}
        </div>
      </div>
      {t.amount > 0 && (
        <div className={`font-heading font-bold tabular-nums text-sm ${config.color}`}>
          {config.sign}{fmtNaira(t.amount)}
        </div>
      )}
    </div>
  );
}
